// src/pages/AboutPage.jsx
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Clock, Instagram } from "lucide-react";
import { useTenant } from "../context/TenantContext";
import { StarRating } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { displayPhone } from "../utils/phone";

export default function AboutPage() {
  const navigate   = useNavigate();
  const { slug }   = useParams();
  const { user }   = useAuth();
  const { tenant } = useTenant();

  if (!tenant) return null;

  return (
    <div className="bg-surface-50 min-h-screen">

      {/* Hero */}
      <section className="py-20 bg-white border-b border-surface-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="badge-brand mx-auto mb-4 inline-flex">Our Story</div>
          <h1 className="font-semibold text-4xl md:text-5xl text-ink-900 mb-5">
            More Than a Service.{" "}
            <span className="text-gradient italic">An Experience.</span>
          </h1>
          <p className="text-ink-500 font-sans text-base max-w-2xl mx-auto leading-relaxed">
            {tenant.name} was built for people who value their time and expect quality.
            We combined professional service with a dead-simple online booking system —
            so you spend less time on the phone and more time getting things done.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { emoji: "🎯", title: "Quality First",     desc: "Every team member is trained to listen to what you want and deliver results that exceed your expectations." },
              { emoji: "⏱️", title: "Respect Your Time", desc: "No waiting rooms, no chaos. Book your slot, show up, and we'll be ready for you." },
              { emoji: "🌿", title: "Professional Grade", desc: "We hold ourselves to a high standard — because you deserve a reliable, consistent experience every time." },
            ].map(({ emoji, title, desc }) => (
              <div key={title} className="card p-7 text-center hover:shadow-card-hover transition-all duration-200">
                <div className="text-4xl mb-4">{emoji}</div>
                <h3 className="font-semibold text-ink-900 text-lg mb-2">{title}</h3>
                <p className="text-ink-400 text-sm font-sans leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-white border-t border-surface-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="badge-brand mx-auto mb-3 inline-flex">The Team</div>
            <h2 className="font-semibold text-3xl md:text-4xl text-ink-900">
              Expert Hands, <span className="text-gradient italic">Every Time</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(tenant.staff || []).map(barber => (
              <div key={barber.id}
                className="card p-7 text-center hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${barber.color}
                                flex items-center justify-center text-white font-mono font-bold
                                text-xl mx-auto mb-4 shadow-sm`}>
                  {barber.initials}
                </div>
                <h3 className="font-semibold text-ink-900 text-lg mb-1">{barber.name}</h3>
                <p className="text-brand-600 text-xs font-mono mb-3">{barber.specialty}</p>
                <p className="text-ink-400 text-sm font-sans leading-relaxed mb-4">{barber.bio}</p>
                <StarRating rating={barber.rating} size="md" />
                <div className="text-ink-400 text-xs mt-1 font-mono">{barber.reviews} reviews</div>
                <button onClick={() => navigate(`/${slug}/book`)}
                  className="btn-primary w-full mt-5 text-sm">
                  Book with {barber.name.split(" ")[0]}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-surface-50 border-t border-surface-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: `${tenant.rating}★`,                    label: "Average Rating"   },
              { value: `${tenant.reviews}+`,                    label: "Happy Clients"    },
              { value: `${tenant.staff?.length || 0}`,          label: "Team Members"     },
              { value: `${tenant.services?.length || 0}`,       label: "Services Offered" },
            ].map(({ value, label }) => (
              <div key={label} className="card p-6">
                <div className="font-semibold text-3xl text-brand-600 mb-1">{value}</div>
                <div className="text-ink-400 text-xs font-sans">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Find us */}
      <section className="py-16 bg-white border-t border-surface-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="badge-brand mx-auto mb-3 inline-flex">Find Us</div>
            <h2 className="font-semibold text-3xl text-ink-900">Get in Touch</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 max-w-4xl mx-auto">
            {[
              { Icon: MapPin,    label: "Address",   value: tenant.address               },
              { Icon: Phone,     label: "Phone",     value: displayPhone(tenant.phone)   },
              { Icon: Clock,     label: "Hours",     value: tenant.hours                 },
              { Icon: Instagram, label: "Instagram", value: tenant.instagram || "@bookyara" },
            ].map(({ Icon, label, value }) => (
              <div key={label} className="card p-6 text-center">
                <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center mx-auto mb-3">
                  <Icon size={18} className="text-brand-500" />
                </div>
                <div className="font-semibold text-ink-700 text-sm mb-1">{label}</div>
                <div className="text-ink-400 text-xs font-sans leading-relaxed">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-surface-50 border-t border-surface-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-semibold text-3xl text-ink-900 mb-4">Ready to Book?</h2>
          <p className="text-ink-400 text-sm font-sans mb-8">
            Book in under 60 seconds. No phone calls, no waiting.
          </p>
          <button
            onClick={() => navigate(user ? `/${slug}/book` : "/signup")}
            className="btn-primary px-10 py-4 text-base inline-flex items-center gap-2">
            {user ? "Book Now" : "Get Started Free"} <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
}
